import axios from "axios";
import { api } from "@/lib/api";

export interface DocumentField {
  key: string; label: string; source: string; section: string;
  required: boolean; manual: boolean; default_value: string | null;
}
/** Country fields in existing data-driven correspondence templates. */
export function isCountryDocumentField(field: Pick<DocumentField, "key" | "source" | "label">): boolean {
  return /(?:^|[._\s])(country|citizenship|nationality|drzava|drzavljanstvo|zemlja)(?:$|[._\s])/.test(
    `${field.key} ${field.source} ${field.label}`.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase(),
  );
}
export interface DocumentTemplate {
  id: string; code: string; name: string; version: number; authority_id: string | null;
  fields: DocumentField[]; sha256: string; has_docx: boolean;
  created_at?: string | null;
  output_formats?: ("docx" | "pdf")[];
  fixed_values?: Record<string, string>;
}
export interface DocumentOptions {
  template_id: string; manual_values: Record<string, string>; authority_id?: string;
  format?: "pdf" | "docx";
}
export interface DocumentPreview {
  template_id: string; template_name: string; template_version: number;
  fields: (DocumentField & { value: string | null; missing: boolean; card_url: string | null; warning?: string | null })[];
  can_generate: boolean; text: string | null;
}
export interface TemplateInspection { found: string[]; defined: string[]; undefined: string[]; unused: string[] }
export type TemplateMetadata = Pick<DocumentTemplate, "code" | "name" | "authority_id" | "fields">;
export const foreignFields = [
  ["parent_name", "Ime roditelja", "text"], ["birth_surname", "Rođeno prezime", "text"],
  ["birth_place", "Mjesto rođenja", "text"], ["birth_country", "Država rođenja", "country"],
  ["marital_status", "Bračno stanje", "text"], ["foreign_address", "Adresa u inozemstvu", "text"],
  ["entry_date", "Datum ulaska u RH", "date"], ["entry_place", "Mjesto ulaska u RH", "text"],
  ["visa_type", "Vrsta vize", "text"], ["visa_number", "Broj vize", "text"],
  ["visa_issue_place", "Mjesto izdavanja vize", "text"], ["visa_valid_until", "Viza vrijedi do", "date"],
  ["education_level", "Stupanj obrazovanja", "text"],
] as const;
export type ForeignKey = typeof foreignFields[number][0];
export type ForeignDetails = Record<ForeignKey, string | null> & { spol: string | null; missing_required: string[] };
export const documentSources: [string, string][] = [
  ["clan.first_name", "Član obitelji: ime"], ["clan.last_name", "Član obitelji: prezime"], ["clan.full_name", "Član obitelji: ime i prezime"],
  ["clan.birth_date", "Član obitelji: datum rođenja"], ["clan.citizenship_iso2", "Član obitelji: državljanstvo"],
  ["clan.oib", "Član obitelji: OIB"], ["clan.passport_number", "Član obitelji: putovnica"], ["clan.address", "Član obitelji: adresa"], ["clan.relationship", "Član obitelji: odnos s nositeljem"],
  ["nositelj.full_name", "Nositelj: ime i prezime (rubrika 12)"], ["nositelj.oib", "Nositelj: OIB (rubrika 12)"],
  ["nositelj.ime", "Nositelj: ime (rubrika 12)"], ["nositelj.prezime", "Nositelj: prezime (rubrika 12)"],
  ["nositelj.datum_rodenja", "Nositelj: datum rođenja (rubrika 12)"],
  ["nositelj.birth_place", "Nositelj: mjesto rođenja (rubrika 12)"], ["nositelj.birth_country", "Nositelj: država rođenja (rubrika 12)"],
  ["nositelj.drzavljanstvo_iso2", "Nositelj: državljanstvo (rubrika 12)"], ["nositelj.broj_putovnice", "Nositelj: putovnica (rubrika 12)"], ["nositelj.adresa_boraviste", "Nositelj: adresa (rubrika 12)"],
  ["radnik.full_name", "Radnik: ime i prezime"], ["radnik.ime", "Radnik: ime"], ["radnik.prezime", "Radnik: prezime"],
  ["radnik.datum_rodenja", "Radnik: datum rođenja"], ["radnik.oib", "Radnik: OIB"],
  ["radnik.drzavljanstvo_iso2", "Radnik: državljanstvo"], ["radnik.broj_putovnice", "Radnik: broj putovnice"],
  ["radnik.adresa_boraviste", "Radnik: adresa boravišta"], ["radnik.datum_pocetka_rada", "Radnik: početak rada"],
  ["radnik.datum_prijave_osiguranja", "Radnik: datum prijave u osiguranje"],
  ["radnik.datum_prestanka", "Radnik: datum prestanka"], ["radnik.spol", "Radnik: spol"],
  ["radnik.radno_mjesto", "Radnik: radno mjesto"], ["radnik.worker_noun", "Radnik: radnik / radnica"],
  ["radnik.born_adjective", "Radnik: rođen / rođena"],
  ...foreignFields.map(([key, label]): [string, string] => [`radnik.${key}`, `Radnik: ${label}`]),
  ["firma.naziv", "Firma: naziv"], ["firma.oib", "Firma: OIB"], ["firma.adresa", "Firma: adresa"],
  ["dozvola.datum_izdavanja", "Dozvola: datum izdavanja"], ["dozvola.datum_isteka", "Dozvola: datum isteka"],
  ["dozvola.broj_dozvole", "Dozvola: broj"], ["predmet.case_number", "Predmet: broj"],
  ["predmet.submitted_on", "Predmet: datum predaje"], ["pu.name", "PU / PP: naziv"], ["pu.address", "PU / PP: poštanska adresa"],
  ["manual.value", "Ručni unos"], ["manual.date", "Ručni datum (zadano danas)"],
  ["manual.previous_address", "Prethodna adresa (ručno)"],
  ["address.registration", "Adresa za prijavu (ručno)"], ["family.value", "Član obitelji (ručno)"],
];
export function sourceLabel(source: string) { return documentSources.find(([key]) => key === source)?.[1] ?? (source.startsWith("family.") ? "Član obitelji (ručno)" : source.startsWith("manual.") ? "Ručni unos" : source); }
export async function getDocumentTemplates(processId?: string) {
  return (await api.get<DocumentTemplate[]>(processId ? `/dozvole/postupci/${processId}/predlosci-dokumenata` : "/dozvole/predlosci-dokumenata")).data;
}
export async function inspectDocumentTemplate(file: File, metadata: TemplateMetadata) {
  const body = new FormData(); body.append("file", file); body.append("metadata", JSON.stringify(metadata));
  return (await api.post<TemplateInspection>("/dozvole/predlosci-dokumenata/provjera", body)).data;
}
export async function uploadDocumentTemplate(file: File, metadata: TemplateMetadata) {
  const body = new FormData(); body.append("file", file); body.append("metadata", JSON.stringify(metadata));
  return (await api.post<DocumentTemplate>("/dozvole/predlosci-dokumenata", body)).data;
}
export async function previewDocument(processId: string, options: DocumentOptions) {
  return (await api.post<DocumentPreview>(`/dozvole/postupci/${processId}/dokumenti/pregled`, options)).data;
}
export async function generateDocument(processId: string, options: DocumentOptions) {
  try { return (await api.post<Blob>(`/dozvole/postupci/${processId}/dokumenti/generiraj`, options, { responseType: "blob" })).data; }
  catch (error) {
    if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
      try { error.response.data = JSON.parse(await error.response.data.text()) as unknown; } catch { /* Croatian fallback in the caller. */ }
    }
    throw error;
  }
}
export async function getForeignDetails(id: string) { return (await api.get<ForeignDetails>(`/radnici/${id}/podaci-stranca`)).data; }
export async function saveForeignDetails(id: string, values: Record<ForeignKey, string | null>) { await api.put(`/radnici/${id}/podaci-stranca`, values); }
export async function patchForeignDetails(id: string, values: Partial<Record<ForeignKey, string | null>>) { await api.patch(`/radnici/${id}/podaci-stranca`, values); }
export function saveDocumentBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a"); link.href = url; link.download = filename;
  document.body.append(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
}
